import { useEffect, useState, useRef } from 'react';
import { Progress } from '../../../ui/common-ui/progress';
import { Button } from '../../../ui/common-ui/button';
import { Cloud, Database, Network, Users, Server, Key, CheckCircle2, Loader2, ChevronDown, Check } from 'lucide-react';
import type { CloudType } from '../types/api';

// Import cloud logos
import awsLogo from '../assets/aws-logo.svg';
import gcpLogo from '../assets/gcp-logo.svg';
import azureLogo from '../assets/azure-logo.svg';

export interface LoadingStep {
  id: string;
  label: string;
  status: 'pending' | 'loading' | 'complete' | 'error';
}

export interface AwsAccountInfo {
  accountId: string;
  accountName: string;
}

export interface AzureSubscriptionInfo {
  subscriptionId: string;
  subscriptionName: string;
}

interface CloudAccounts {
  gcp: string[];
  aws: AwsAccountInfo[];
  azure: AzureSubscriptionInfo[];
}

interface Tenant {
  tenantName: string;
  displayName: string;
}

interface LoadingPageProps {
  phase?: 'selection' | 'loading';
  tenants?: Tenant[];
  cloudAccounts?: CloudAccounts;
  isLoadingAccounts?: boolean;
  onStartLoading?: (tenantName: string, cloudType: CloudType, cloudId: string) => void;
  steps?: LoadingStep[];
  progress?: number;
}

const CLOUD_CONFIG = {
  AWS: { logo: awsLogo, label: 'AWS', color: 'from-orange-500 to-amber-500', glow: 'shadow-orange-500/30' },
  GCP: { logo: gcpLogo, label: 'Google Cloud', color: 'from-blue-500 to-cyan-500', glow: 'shadow-blue-500/30' },
  AZURE: { logo: azureLogo, label: 'Azure', color: 'from-sky-500 to-blue-600', glow: 'shadow-sky-500/30' },
};

// Custom Dropdown Component
interface CustomDropdownProps {
  label: string;
  value: string | null;
  placeholder: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
}

function CustomDropdown({ label, value, placeholder, options, onChange, disabled, isLoading }: CustomDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="space-y-1.5" ref={dropdownRef}>
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider text-center block">
        {label}
      </label>
      <div className="relative">
        <button
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`
            w-full h-14 px-5 rounded-2xl border-2 transition-all duration-300 flex items-center justify-between
            bg-card backdrop-blur-sm
            ${isOpen
              ? 'border-primary shadow-lg shadow-primary/10 ring-4 ring-primary/5'
              : 'border-border hover:border-primary/30 hover:shadow-md'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <span className={`flex-1 text-center font-medium ${value ? 'text-foreground' : 'text-muted-foreground'}`}>
            {isLoading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </span>
            ) : value || placeholder}
          </span>
          <ChevronDown
            className={`w-5 h-5 text-muted-foreground transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Dropdown Menu */}
        <div
          className={`
            absolute top-full left-0 right-0 mt-2 z-50
            bg-card border-2 border-border/50 rounded-2xl shadow-2xl shadow-black/10
            overflow-hidden transition-all duration-300 origin-top
            ${isOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'}
          `}
        >
          <div className="max-h-64 overflow-auto py-2">
            {options.length === 0 ? (
              <div className="px-4 py-3 text-center text-muted-foreground text-sm">
                No options available
              </div>
            ) : (
              options.map((option, index) => (
                <button
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`
                    w-full px-5 py-3 text-left transition-all duration-200 flex items-center gap-3
                    hover:bg-muted/50 hover:pl-7
                    ${value === option.value ? 'text-primary font-medium' : 'text-foreground'}
                  `}
                  style={{ animationDelay: `${index * 30}ms` }}
                >
                  <div className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all
                    ${value === option.value
                      ? 'border-primary bg-primary'
                      : 'border-border'}
                  `}>
                    {value === option.value && <Check className="w-3 h-3 text-primary-foreground" />}
                  </div>
                  <span className="flex-1">{option.label}</span>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function LoadingPage({
  phase = 'loading',
  tenants = [],
  cloudAccounts = { gcp: [], aws: [], azure: [] },
  isLoadingAccounts = false,
  onStartLoading,
  steps,
  progress: externalProgress
}: LoadingPageProps) {
  const [internalProgress, setInternalProgress] = useState(0);
  const [activeOrb, setActiveOrb] = useState(0);

  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [selectedCloudType, setSelectedCloudType] = useState<CloudType | null>(null);
  const [selectedCloudId, setSelectedCloudId] = useState<string | null>(null);

  const defaultSteps: LoadingStep[] = [
    { id: 'users', label: 'User Nodes', status: 'pending' },
    { id: 'sa', label: 'Service Accounts', status: 'pending' },
    { id: 'resources', label: 'Resources', status: 'pending' },
    { id: 'roles', label: 'Roles & Policies', status: 'pending' },
    { id: 'edges', label: 'Connections', status: 'pending' },
  ];

  const loadingSteps = steps || defaultSteps;
  const progress = externalProgress ?? internalProgress;

  useEffect(() => {
    if (tenants.length > 0 && !selectedTenant) {
      setSelectedTenant(tenants[0]);
    }
  }, [tenants, selectedTenant]);

  useEffect(() => {
    setSelectedCloudType(null);
    setSelectedCloudId(null);
  }, [selectedTenant]);

  const getAvailableCloudIds = (): { value: string; label: string }[] => {
    if (!selectedCloudType) return [];
    switch (selectedCloudType) {
      case 'AWS':
        return cloudAccounts.aws.map(a => ({ value: a.accountId, label: a.accountName || a.accountId }));
      case 'GCP':
        return cloudAccounts.gcp.map(id => ({ value: id, label: id }));
      case 'AZURE':
        return cloudAccounts.azure.map(a => ({ value: a.subscriptionId, label: a.subscriptionName || a.subscriptionId }));
      default:
        return [];
    }
  };

  const getAvailableCloudTypes = (): CloudType[] => {
    const types: CloudType[] = [];
    if (cloudAccounts.aws.length > 0) types.push('AWS');
    if (cloudAccounts.gcp.length > 0) types.push('GCP');
    if (cloudAccounts.azure.length > 0) types.push('AZURE');
    return types;
  };

  useEffect(() => {
    if (phase !== 'loading' || externalProgress !== undefined) return;

    const interval = setInterval(() => {
      setInternalProgress((prev) => {
        if (prev >= 95) return 95;
        return Math.min(prev + 2, 95);
      });
    }, 100);

    return () => clearInterval(interval);
  }, [phase, externalProgress]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveOrb((prev) => (prev + 1) % 5);
    }, 600);
    return () => clearInterval(interval);
  }, []);

  const getStepIcon = (step: LoadingStep, index: number) => {
    const icons = [Users, Server, Database, Key, Network];
    const Icon = icons[index % icons.length];

    if (step.status === 'complete') {
      return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
    }
    if (step.status === 'loading') {
      return <Loader2 className="w-4 h-4 text-primary animate-spin" />;
    }
    return <Icon className="w-4 h-4 text-muted-foreground" />;
  };

  const handleStartLoading = () => {
    if (selectedTenant && selectedCloudType && selectedCloudId && onStartLoading) {
      onStartLoading(selectedTenant.tenantName, selectedCloudType, selectedCloudId);
    }
  };

  const handleCloudTypeClick = (cloudType: CloudType, isAvailable: boolean) => {
    if (!isAvailable || isLoadingAccounts) return;

    // Toggle: if already selected, unselect it
    if (selectedCloudType === cloudType) {
      setSelectedCloudType(null);
      setSelectedCloudId(null);
    } else {
      setSelectedCloudType(cloudType);
      setSelectedCloudId(null);
    }
  };

  const canProceed = selectedTenant && selectedCloudType && selectedCloudId;

  const tenantOptions = tenants.map(t => ({ value: t.tenantName, label: t.displayName || t.tenantName }));
  const cloudIdOptions = getAvailableCloudIds();

  return (
    <div className="fixed inset-0 bg-background overflow-y-auto overflow-x-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), 
                             linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />

        <div className="absolute top-1/4 left-1/4 w-80 h-80 bg-primary/8 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/8 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '0.5s' }} />
      </div>

      <div className="relative z-10 flex min-h-full w-full items-center justify-center px-4 py-6 sm:px-8 sm:py-8">
        <div className="w-full max-w-lg">
          {/* Central Animation */}
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="relative [--nexus-size:clamp(7.5rem,15vh,10rem)] w-[var(--nexus-size)] h-[var(--nexus-size)]">
            <div className="absolute inset-0 rounded-full border-2 border-dashed border-primary/20 animate-spin" style={{ animationDuration: '8s' }} />
            <div className="absolute inset-2 rounded-full border border-primary/30 animate-spin" style={{ animationDuration: '6s', animationDirection: 'reverse' }} />

            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className={`absolute w-3 h-3 rounded-full transition-all duration-300 ${activeOrb === i ? 'bg-primary scale-125' : 'bg-primary/40'
                  }`}
                style={{
                  top: '50%',
                  left: '50%',
                  transform: `rotate(${i * 72}deg) translateY(calc(var(--nexus-size) * -0.425)) translateX(-50%)`,
                }}
              />
            ))}

            <div className="absolute inset-[20%] rounded-full bg-card border border-border flex items-center justify-center shadow-lg">
              <Network className="w-[clamp(2.25rem,4.5vh,3rem)] h-[clamp(2.25rem,4.5vh,3rem)] text-primary" strokeWidth={1.5} />
            </div>
          </div>
          </div>

          {/* Title */}
          <div className="text-center mb-6 sm:mb-8 animate-fade-in">
            <h1 className="text-2xl font-semibold text-foreground mb-2 tracking-tight">
              Whiteswan Nexus
            </h1>
            <p className="text-sm text-muted-foreground">
              {phase === 'selection'
                ? 'Select your cloud environment to visualize'
                : 'Initializing cloud infrastructure visualization'}
            </p>
          </div>

          {phase === 'selection' ? (
            <>
              <div className="space-y-4 mb-4">
                {/* Tenant Selector */}
                <CustomDropdown
                  label="Tenant"
                  value={selectedTenant?.displayName || selectedTenant?.tenantName || null}
                  placeholder="Select tenant"
                  options={tenantOptions}
                  onChange={(value) => {
                    const tenant = tenants.find(t => t.tenantName === value);
                    if (tenant) setSelectedTenant(tenant);
                  }}
                  disabled={isLoadingAccounts || tenants.length === 0}
                  isLoading={isLoadingAccounts}
                />
              </div>

              {/* Cloud Provider Label */}
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider text-center block mb-3">
                Cloud Provider
              </label>

              {/* Cloud Provider Boxes */}
              <div className="flex justify-center gap-3 sm:gap-4 mb-4">
                {(['AWS', 'GCP', 'AZURE'] as CloudType[]).map((cloudType) => {
                  const config = CLOUD_CONFIG[cloudType];
                  const isAvailable = getAvailableCloudTypes().includes(cloudType);
                  const isSelected = selectedCloudType === cloudType;

                  return (
                    <button
                      key={cloudType}
                      onClick={() => handleCloudTypeClick(cloudType, isAvailable)}
                      disabled={!isAvailable || isLoadingAccounts}
                      className={`
                        relative flex items-center justify-center
                        w-[clamp(6rem,13vh,8.5rem)] h-[clamp(6rem,13vh,8.5rem)]
                        lg:w-[clamp(7rem,16vh,10rem)] lg:h-[clamp(7rem,16vh,10rem)]
                        rounded-xl sm:rounded-2xl border-2 transition-all duration-300 group overflow-hidden flex-shrink-0
                        ${isSelected
                          ? `border-transparent bg-gradient-to-br ${config.color} shadow-xl ${config.glow}`
                          : isAvailable
                            ? 'border-border bg-card backdrop-blur-sm hover:border-primary/30 hover:shadow-lg hover:scale-105'
                            : 'border-border/30 bg-card/30 opacity-40 cursor-not-allowed'}
                      `}
                    >
                      {/* Selected checkmark */}
                      {isSelected && (
                        <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-white/20 flex items-center justify-center animate-scale-in">
                          <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                        </div>
                      )}

                      {/* Logo only */}
                      <img
                        src={config.logo}
                        alt={config.label}
                        className={`w-[clamp(2.5rem,5vh,3.25rem)] h-[clamp(2.5rem,5vh,3.25rem)] lg:w-[clamp(3rem,6vh,4rem)] lg:h-[clamp(3rem,6vh,4rem)] transition-all duration-300 ${isSelected ? 'brightness-0 invert' : ''}`}
                      />

                      {/* Hover glow effect */}
                      {isAvailable && !isSelected && (
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                          <div className={`absolute inset-0 bg-gradient-to-br ${config.color} opacity-5`} />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="space-y-4">
                {/* Cloud Account/Project Selector */}
                <CustomDropdown
                  label={selectedCloudType === 'GCP' ? 'Project ID' : selectedCloudType === 'AZURE' ? 'Subscription' : 'Account ID'}
                  value={cloudIdOptions.find(opt => opt.value === selectedCloudId)?.label || selectedCloudId}
                  placeholder={selectedCloudType ? 'Select account' : 'Select cloud provider first'}
                  options={cloudIdOptions}
                  onChange={setSelectedCloudId}
                  disabled={!selectedCloudType || cloudIdOptions.length === 0}
                />

                {/* Load Button */}
                <Button
                  onClick={handleStartLoading}
                  disabled={!canProceed}
                  className={`
                    w-full h-14 mt-2 font-medium text-base rounded-2xl transition-all duration-300
                    ${canProceed
                      ? 'bg-gradient-to-r from-primary to-primary/80 hover:shadow-lg hover:shadow-primary/25 hover:scale-[1.02]'
                      : ''}
                  `}
                  size="lg"
                >
                  {isLoadingAccounts ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Loading accounts...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Cloud className="w-5 h-5" />
                      Load Cloud Infrastructure
                    </span>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Loading Steps */}
              <div className="space-y-2 mb-6 sm:mb-8">
                {loadingSteps.map((step, index) => (
                  <div
                    key={step.id}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all duration-300 ${step.status === 'loading'
                      ? 'bg-primary/5 border-primary/20'
                      : step.status === 'complete'
                        ? 'bg-emerald-500/5 border-emerald-500/20'
                        : 'bg-card/50 border-border/50'
                      }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="flex-shrink-0">
                      {getStepIcon(step, index)}
                    </div>
                    <span className={`text-sm font-medium ${step.status === 'complete' ? 'text-emerald-600 dark:text-emerald-400' :
                      step.status === 'loading' ? 'text-primary' : 'text-muted-foreground'
                      }`}>
                      {step.label}
                    </span>
                    {step.status === 'loading' && (
                      <span className="ml-auto text-xs text-primary/70">Loading...</span>
                    )}
                    {step.status === 'complete' && (
                      <span className="ml-auto text-xs text-emerald-500">Done</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Progress Bar */}
              <div className="space-y-3 animate-fade-in">
                <div className="relative">
                  <Progress value={progress} className="h-2" />
                  <div
                    className="absolute top-0 left-0 h-2 bg-gradient-to-r from-transparent via-white/20 to-transparent rounded-full"
                    style={{
                      width: `${progress}%`,
                      animation: 'shimmer 2s infinite'
                    }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">
                    {progress < 100 ? 'Loading cloud data...' : 'Ready!'}
                  </span>
                  <span className="font-semibold text-primary tabular-nums">{Math.round(progress)}%</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0%, 100% { opacity: 0; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
